import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import useReveal from '../hooks/useReveal';
import './Contact.css';
import { trackEvent } from '../analytics';
import { EMAILJS_CONFIG, isEmailJsConfigured } from '../emailConfig';

const RECEIVING_EMAIL = 'inivayal.agritech@gmail.com';

const STATUS = {
  IDLE: 'idle',
  SENDING: 'sending',
  SENT: 'sent',
  ERROR: 'error',
};

export default function Contact() {
  const ref = useReveal();
  const [form, setForm] = useState({ name: '', email: '', role: 'Investor', message: '' });
  const [status, setStatus] = useState(STATUS.IDLE);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const sendViaMailto = () => {
    const subject = encodeURIComponent(`[Inivayal Website] ${form.role} enquiry from ${form.name}`);
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\nI am a: ${form.role}\n\nMessage:\n${form.message}`
    );
    window.location.href = `mailto:${RECEIVING_EMAIL}?subject=${subject}&body=${body}`;
    setStatus(STATUS.SENT);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    trackEvent('contact_submit', { role: form.role });

    // Preferred path: send silently via EmailJS, straight from the browser,
    // no mail client popup. Falls back to mailto if EmailJS hasn't been
    // configured yet (see src/emailConfig.js) or if the request fails.
    if (!isEmailJsConfigured()) {
      sendViaMailto();
      return;
    }

    setStatus(STATUS.SENDING);
    try {
      await emailjs.send(
        EMAILJS_CONFIG.serviceId,
        EMAILJS_CONFIG.templateId,
        {
          from_name: form.name,
          from_email: form.email,
          role: form.role,
          message: form.message,
          to_email: RECEIVING_EMAIL,
        },
        { publicKey: EMAILJS_CONFIG.publicKey }
      );
      setStatus(STATUS.SENT);
      trackEvent('contact_send_success', { role: form.role });
      setForm({ name: '', email: '', role: 'Investor', message: '' });
    } catch (err) {
      setStatus(STATUS.ERROR);
      trackEvent('contact_send_error', { role: form.role });
    }
  };

  return (
    <section className="section contact" id="contact">
      <div className="container">
        <div className="reveal" ref={ref}>
          <p className="eyebrow">Get in touch</p>
          <h2 className="section-heading">Building the future of agri-education. Let's talk.</h2>
          <p className="section-sub">
            Whether you're an investor, an institution, or a mentor — we'd like to hear from you.
          </p>
        </div>

        <div className="contact__grid">
          <form className="contact__form" onSubmit={handleSubmit}>
            <div className="contact__row">
              <label>
                Name
                <input required type="text" value={form.name} onChange={update('name')} placeholder="Your full name" />
              </label>
              <label>
                Email
                <input required type="email" value={form.email} onChange={update('email')} placeholder="you@example.com" />
              </label>
            </div>

            <label>
              I am a
              <select value={form.role} onChange={update('role')}>
                <option>Investor</option>
                <option>Institution / University</option>
                <option>Training Institute</option>
                <option>Aspiring Farmer / Individual</option>
                <option>Mentor / Advisor</option>
                <option>Other</option>
              </select>
            </label>

            <label>
              Message
              <textarea required rows={5} value={form.message} onChange={update('message')} placeholder="Tell us a bit about what you're looking for..." />
            </label>

            <button type="submit" className="btn btn-primary contact__submit" disabled={status === STATUS.SENDING}>
              {status === STATUS.SENDING ? 'Sending…' : 'Send Message'}
            </button>

            {status === STATUS.SENT && (
              <p className="contact__sent">
                {isEmailJsConfigured()
                  ? `Thanks — your message has been sent to ${RECEIVING_EMAIL}.`
                  : `Your email app should now be open with this message ready to send to ${RECEIVING_EMAIL}.`}
              </p>
            )}
            {status === STATUS.ERROR && (
              <p className="contact__error">
                Something went wrong sending that. Please try again, or email{' '}
                <a href={`mailto:${RECEIVING_EMAIL}`}>{RECEIVING_EMAIL}</a> directly.
              </p>
            )}
          </form>

          <div className="contact__side">
            <div className="contact__card">
              <h4>Email</h4>
              <a href={`mailto:${RECEIVING_EMAIL}`}>{RECEIVING_EMAIL}</a>
            </div>
            <div className="contact__card">
              <h4>Phone</h4>
              <a href="tel:+918015153380">+91 80151 53380</a>
              <a href="tel:+918807562379">+91 88075 62379</a>
            </div>
            <div className="contact__card">
              <h4>Instagram</h4>
              <a href="https://instagram.com/inivayal.agritech" target="_blank" rel="noreferrer">
                @inivayal.agritech
              </a>
            </div>
            <div className="contact__card">
              <h4>Headquarters</h4>
              <span>Madurai, Tamil Nadu, India</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
